/**
 * Card expand: a listing card in a masonry grid opens inside the grid
 *
 * The card grows to two columns (_card-expand.scss) and shows a panel below
 * it with the entry's article - text, slideshow, gallery, details - which
 * htmx fetches from @@booost-card-expand on the first open. The card itself
 * keeps showing title, date, tags and description; the panel head is the
 * close button only. Shuffle packs the other items around the grown card -
 * one layout() after every size change.
 * One open card per grid; a second click, the close button at the end of
 * the article or Escape closes. The card's read-more control opens it on
 * the text, a click on the card itself on the image.
 * See booost.cms docs/features/CARD_EXPAND.md.
 *
 * @requires https://github.com/Vestride/Shuffle (optional: without the
 *   instance the class still toggles, the card just grows in its row)
 * @requires https://htmx.org (optional: without it the panel opens empty)
 *
 * The article's own widgets (gallery, carousel, image skeletons, AOS) are
 * set up by their components through on-fragment.js, on htmx:load.
 */

export default (() => {
  const ITEM = '.masonry-grid-item[data-expand]'

  // The card's read-more control (factory: card-expand-more) opens the card
  // and lands on the start of the text; the close button at the end of the
  // article closes it and brings the card back into view.
  const MORE = '.card-expand-more'
  const CLOSE = '.card-expand-close'

  // Clicks on these inside a card keep doing their own thing
  const INTERACTIVE =
    'a, button:not(.card-expand-toggle), input, select, textarea, label'

  // one layout per grid and frame, however many images or resizes ask for it
  const relayout = (item) => {
    const grid = item.closest('.masonry-grid')
    const shuffle = grid && grid._shuffle
    if (!shuffle || grid._expandFrame) return
    grid._expandFrame = requestAnimationFrame(() => {
      grid._expandFrame = 0
      // masonry-grid.js passes the sizer as a selector, which Shuffle turns
      // into the FIRST item once. Measured on an open card it would be twice
      // as wide and halve the column count, so the sizer has to be an item
      // that is not open at the time of every layout - and one of this grid,
      // not an item of a grid inside an open card's article (:scope >).
      const sizer = grid.querySelector(
        ':scope > .masonry-grid-item:not(.is-expanded)'
      )
      if (sizer) shuffle.options.sizer = sizer
      shuffle.layout()
    })
  }

  // The article keeps changing its height after the swap: lazy gallery
  // images, a slideshow swiper builds, fonts. One observer per panel lays
  // the grid out whenever it does (only while the card is open: a closed
  // panel is hidden, its size 0).
  const watch = (item, panel) => {
    if (panel._expandObserver || !('ResizeObserver' in window)) return
    panel._expandObserver = new ResizeObserver(() => {
      if (item.classList.contains('is-expanded')) relayout(item)
    })
    panel._expandObserver.observe(panel)
  }

  const parts = (item) => {
    const toggle = item.querySelector(':scope > .card-expand-toggle')
    const panel =
      toggle && document.getElementById(toggle.getAttribute('aria-controls'))
    return { toggle, panel }
  }

  // The card's bottom margin is the grid's row gap (the blog card's mb-5).
  // Open, the panel continues the card, so the gap moves below the panel.
  // The panel's side and bottom padding follow the card body's, whatever
  // utilities the view or the tile's css_class put on it (p-3 p-sm-4, ...),
  // so the article's text lines up with the card's own text. Measured, not
  // copied as classes: the padding is responsive. Re-measured on resize.
  const PADDING = ['paddingLeft', 'paddingRight', 'paddingBottom']
  // The card's text body (title, date, description), not a card-body laid
  // over the image: the blog card's badge overlay is position-absolute with
  // its own padding - measured, it indented the article by 14 px
  // (edmundaut, 2026-10-28).
  const textBody = (item) =>
    [...item.querySelectorAll(':scope > .card .card-body')].find(
      (el) => getComputedStyle(el).position !== 'absolute'
    )
  const alignPadding = (item, panel) => {
    const body = textBody(item)
    if (!body) return
    const style = getComputedStyle(body)
    PADDING.forEach((side) => {
      panel.style[side] = style[side]
    })
  }
  const joinCard = (item, panel) => {
    const card = item.firstElementChild
    if (!card || !card.classList.contains('card')) return
    panel.style.marginBottom = getComputedStyle(card).marginBottom
    card.style.setProperty('margin-bottom', '0', 'important')
    alignPadding(item, panel)
  }
  const partCard = (item, panel) => {
    const card = item.firstElementChild
    if (card) card.style.removeProperty('margin-bottom')
    if (!panel) return
    panel.style.marginBottom = ''
    PADDING.forEach((side) => {
      panel.style[side] = ''
    })
  }
  window.addEventListener('resize', () => {
    document.querySelectorAll(ITEM + '.is-expanded').forEach((item) => {
      const { panel } = parts(item)
      if (panel) alignPadding(item, panel)
    })
  })

  const close = (item, focus) => {
    const { toggle, panel } = parts(item)
    item.classList.remove('is-expanded')
    partCard(item, panel)
    if (panel) panel.hidden = true
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false')
      if (focus) toggle.focus({ preventScroll: true })
    }
  }

  const open = (item, to = 'card') => {
    const { toggle, panel } = parts(item)
    if (!toggle || !panel) return

    // one open card per grid
    const grid = item.closest('.masonry-grid')
    if (grid) {
      grid.querySelectorAll(ITEM + '.is-expanded').forEach((other) => {
        if (other !== item) close(other, false)
      })
    }

    item.classList.add('is-expanded')
    joinCard(item, panel)
    panel.hidden = false
    toggle.setAttribute('aria-expanded', 'true')

    // hx-trigger="booost:expand once": htmx fetches the article the first
    // time only; an entry with nothing to show gets an empty answer
    if (window.htmx && panel.hasAttribute('hx-get')) {
      window.htmx.trigger(panel, 'booost:expand')
    }

    watch(item, panel)
    relayout(item)
    // remembered for the second follow once the article has arrived; the
    // text starts with the card's title under the image, the panel follows
    item._expandTo = (to === 'text' && (textBody(item) || panel)) || item
    followToTop(item, FOLLOW_MS, item._expandTo)
  }

  // The bottom edge of whatever bar is fixed or sticky across the top of
  // the viewport (the theme's navbar; Plone's toolbar when it is docked on
  // top). A bar: wide, not tall - the toolbar docked on the left is fixed
  // too, but it is a column the full height of the window.
  const stickyOffset = () => {
    let offset = 0
    document.querySelectorAll('header, .navbar, #edit-zone').forEach((el) => {
      const style = getComputedStyle(el)
      if (style.position !== 'fixed' && style.position !== 'sticky') return
      const rect = el.getBoundingClientRect()
      const bar =
        rect.width >= window.innerWidth / 2 &&
        rect.height < window.innerHeight / 2
      if (bar && rect.top <= 1 && rect.bottom > offset) offset = rect.bottom
    })
    return offset
  }

  // Keep the top of the card (its image) right below the sticky header
  // while the grid settles: Shuffle moves the items with a transition
  // after relayout(), closing the card that was open above shifts this one
  // up, and the article arriving makes the page long enough to scroll to.
  // So the page follows the card frame by frame instead of scrolling once
  // to a position it has not reached yet - until it has held still for a
  // few frames or the time is up, and never against the user's own
  // scrolling.
  // Shuffle starts its move about 100 ms after the layout call and takes
  // 250 ms, so the card is still for a moment before it moves: hold on for
  // at least FOLLOW_MIN_MS, stop once the card has held still after that.
  const FOLLOW_MS = 1500
  const FOLLOW_MIN_MS = 700
  // `anchor` is what lands under the header: the card (a click on the card,
  // its image first) or its text body (the read-more control: title first).
  const followToTop = (item, ms = FOLLOW_MS, anchor = item) => {
    if (item._follow) return
    const started = performance.now()
    let still = 0
    let stop = false
    const abort = () => {
      stop = true
    }
    window.addEventListener('wheel', abort, { once: true, passive: true })
    window.addEventListener('touchstart', abort, { once: true, passive: true })
    const step = () => {
      if (stop || !item.classList.contains('is-expanded')) {
        item._follow = false
        return
      }
      const top = anchor.getBoundingClientRect().top + window.scrollY
      const max = document.documentElement.scrollHeight - window.innerHeight
      const target = Math.max(
        0,
        Math.min(Math.round(top - stickyOffset() - 16), max)
      )
      if (Math.abs(window.scrollY - target) > 1) {
        window.scrollTo(0, target)
        still = 0
      } else {
        still += 1
      }
      const elapsed = performance.now() - started
      const settled = elapsed >= Math.min(FOLLOW_MIN_MS, ms) && still >= 12
      if (elapsed < ms && !settled) {
        requestAnimationFrame(step)
      } else {
        item._follow = false
      }
    }
    item._follow = true
    requestAnimationFrame(step)
  }

  // After the close button: the card is up the page, the long article gone
  // from below it - scroll it back under the header once the grid has
  // settled (Shuffle's move, see FOLLOW_MIN_MS).
  const backToCard = (item) => {
    setTimeout(() => {
      const top = item.getBoundingClientRect().top + window.scrollY
      if (item.getBoundingClientRect().top < stickyOffset()) {
        window.scrollTo({
          top: Math.max(0, Math.round(top - stickyOffset() - 16)),
          behavior: 'smooth',
        })
      }
    }, 400)
  }

  const toggleItem = (item) => {
    if (item.classList.contains('is-expanded')) {
      close(item, false)
      relayout(item)
    } else {
      open(item)
    }
  }

  const closeAll = (except) => {
    document.querySelectorAll(ITEM + '.is-expanded').forEach((item) => {
      if (item === except) return
      close(item, false)
      relayout(item)
    })
  }

  // Delegated, so entries a "load more" button adds later take part too
  document.addEventListener('click', (e) => {
    const item = e.target.closest(ITEM)
    if (item === null) {
      // a click anywhere else closes the open card - but not a click in a
      // lightbox, which lightGallery appends to <body> for the article's
      // gallery; the lightbox has its own close
      if (!e.target.closest('.lg-container, .lg-outer, .lg-backdrop')) {
        closeAll(null)
      }
      return
    }

    // the close button at the end of the article
    if (e.target.closest(CLOSE)) {
      e.preventDefault()
      close(item, true)
      relayout(item)
      backToCard(item)
      return
    }
    // the panel is for reading: a click in it neither closes nor opens
    if (e.target.closest('.card-expand')) return
    // the read-more control: open on the text; on an open card it is just
    // the way to the text again
    if (e.target.closest(MORE)) {
      e.preventDefault()
      if (item.classList.contains('is-expanded')) {
        followToTop(item, 600, textBody(item) || parts(item).panel)
      } else {
        open(item, 'text')
      }
      return
    }
    // the toggle itself: Enter and Space arrive here as a click as well
    if (
      !e.target.closest('.card-expand-toggle') &&
      e.target.closest(INTERACTIVE)
    ) {
      return
    }
    e.preventDefault()
    toggleItem(item)
  })

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    // Escape in an open lightbox (a gallery inside the article) closes the
    // lightbox only; lightGallery listens on window, after this listener,
    // and marks the open state with lg-on on <html>
    if (document.documentElement.classList.contains('lg-on')) return
    // one open card per grid, but a page can hold several grids
    document.querySelectorAll(ITEM + '.is-expanded').forEach((item) => {
      close(item, true)
      relayout(item)
    })
  })

  // The article has arrived: the card is taller now, and taller again once
  // its images have loaded. The ResizeObserver of watch() sees all of that;
  // the load listeners are for browsers without one.
  // The article's headings start at h2 on its own page; under a card whose
  // title is an h3 that breaks the outline, and the theme's h2 is sized for
  // a page. Shift them so the article's top level sits one below the card
  // title (h2 -> h4, h3 -> h5, ...), ids and attributes kept, so the table
  // of contents still finds them. Here and not on the server: the tiles are
  // rendered after the view by the transform chain.
  const demoteHeadings = (item, root) => {
    const title = item.querySelector('.card-title, .card-expand-toggle')
    const base =
      title && /^H[1-6]$/.test(title.tagName) ? Number(title.tagName[1]) : 2
    const headings = [...root.querySelectorAll('h1, h2, h3, h4, h5, h6')]
    if (!headings.length) return
    const top = Math.min(...headings.map((h) => Number(h.tagName[1])))
    const shift = base + 1 - top
    if (shift <= 0) return
    headings.forEach((h) => {
      const level = Math.min(6, Number(h.tagName[1]) + shift)
      const el = document.createElement('h' + level)
      for (const { name, value } of h.attributes) el.setAttribute(name, value)
      while (h.firstChild) el.appendChild(h.firstChild)
      h.replaceWith(el)
    })
  }

  document.addEventListener('htmx:afterSwap', (e) => {
    const item = e.target.closest && e.target.closest(ITEM)
    if (!item) return
    demoteHeadings(item, e.target)
    relayout(item)
    followToTop(item, 600, item._expandTo || item)
    e.target.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', () => relayout(item), { once: true })
      }
    })
  })
})()
